import React, { useCallback, useEffect, useRef, useState } from "react";
import "./App.css";
import HeaderBar from "./components/HeaderBar";
import StormAtlasAdminPanel from "./components/StormAtlasAdminPanel";
import StormAtlasRadarPanel from "./components/StormAtlasRadarPanel";
import WhatsNewModal from "./components/WhatsNewModal";

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 32, color: "#ef4444", background: "#0c0f14", fontFamily: "monospace", fontSize: 14, whiteSpace: "pre-wrap" }}>
          <strong>Render error — please share this with support:</strong>
          {"\n\n"}{String(this.state.error)}
        </div>
      );
    }
    return this.props.children;
  }
}

const API_BASE = process.env.REACT_APP_API_BASE ?? "/api";
const POLL_MS     = 30_000;
const FETCH_TIMEOUT_MS = 10_000;

// activeSite: null → idle (no site selected). A non-null value fetches
// the manifest for that ICAO site.
function useManifest(activeSite, limit) {
  const [manifest, setManifest] = useState(null);
  const [error, setError]       = useState(null);
  const [loading, setLoading]   = useState(false);

  const abortRef    = useRef(null);
  const fetchingRef = useRef(false);
  const genRef      = useRef(0);

  const manifestUrlRef = useRef(null);
  manifestUrlRef.current = activeSite
    ? `${API_BASE}/radar/manifest?site=${activeSite}&limit=${limit}`
    : null;

  const poll = useCallback(async () => {
    if (fetchingRef.current) return;
    if (!manifestUrlRef.current) return;        // no site selected — skip fetch

    abortRef.current?.abort();
    const controller    = new AbortController();
    abortRef.current    = controller;
    fetchingRef.current = true;
    const gen           = genRef.current;

    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    try {
      const res = await fetch(manifestUrlRef.current, {
        signal: controller.signal,
        cache:  "no-store",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (gen !== genRef.current) return;

      if (!data || !Array.isArray(data.frames)) {
        throw new Error("Malformed manifest (missing frames)");
      }

      setManifest(data);
      setError(null);
    } catch (err) {
      if (gen !== genRef.current) return;
      if (err.name === "AbortError") {
        setError("Request timed out");
      } else {
        setError(err.message || "Failed to fetch manifest");
      }
    } finally {
      clearTimeout(timeout);
      if (gen === genRef.current) {
        fetchingRef.current = false;
        setLoading(false);
      }
    }
  }, []); // stable identity — reads URL from ref

  // On activeSite change: abort the in-flight request, bump generation so its
  // stale response is discarded, reset lock/loading, and immediately start a
  // fresh poll with the updated URL (already written to manifestUrlRef above).
  useEffect(() => {
    genRef.current     += 1;
    abortRef.current?.abort();
    fetchingRef.current = false;
    setManifest(null);                // clear stale manifest immediately on site switch
    setError(null);
    if (!activeSite) {
      setLoading(false);
      return;
    }
    setLoading(true);
    poll();
  }, [activeSite, limit, poll]);

  // Steady-state polling — poll identity is stable so this mounts once.
  useEffect(() => {
    const id = setInterval(poll, POLL_MS);
    return () => {
      clearInterval(id);
      abortRef.current?.abort();
    };
  }, [poll]);

  const retry = useCallback(() => {
    genRef.current += 1;          // invalidate any in-flight request
    abortRef.current?.abort();
    fetchingRef.current = false;
    setError(null);
    setLoading(true);
    poll();
  }, [poll]);

  return { manifest, error, loading, retry };
}

export default function App() {
  // activeSite owns the actual radar data feed identity for NEXRAD.
  // null = idle (no site selected, no manifest fetch). Set by user action only.
  const [activeSite, setActiveSite] = useState(
    () => localStorage.getItem("sa_activeSite") || null
  );
  const [frameLimit, setFrameLimit] = useState(
    () => parseInt(localStorage.getItem("stormatlas.frameLimit") || "10", 10)
  );
  useEffect(() => {
    localStorage.setItem("stormatlas.frameLimit", String(frameLimit));
  }, [frameLimit]);
  const { manifest, error, loading, retry } = useManifest(activeSite, frameLimit);
  const [view, setView]              = useState("radar"); // "radar" | "admin"

  // Called by StormAtlasRadarPanel when a NEXRAD site marker is clicked.
  // siteId=null means the user deselected — returns to idle state (no radar).
  // MRMS site clicks are handled entirely inside the panel and never reach here.
  const handleRadarSiteSelect = useCallback((siteId) => {
    setActiveSite(siteId || null);
  }, []);

  useEffect(() => {
    if (activeSite) {
      localStorage.setItem("sa_activeSite", activeSite);
    } else {
      localStorage.removeItem("sa_activeSite");
    }
  }, [activeSite]);

  const displayError = error?.includes("404")
    ? `No radar data available${activeSite ? ` for ${activeSite}` : ""}.`
    : error;

  const status = (loading && !manifest) ? "loading"
    : manifest?.png_ready              ? "ready"
    : displayError                     ? "error"
    :                                    "no-data";

  return (
    <div className={`sa-app${view === "radar" ? " sa-app--radar" : ""}`}>

      {/* Header and nav are hidden in radar mode via .sa-app--radar CSS */}
      <HeaderBar
        site={activeSite ?? manifest?.site ?? null}
        lastUpdated={manifest?.last_success_at ?? null}
        status={status}
      />
      <nav className="sa-nav" aria-label="View selector">
        <button
          className={`sa-nav-btn${view === "radar" ? " sa-nav-btn--active" : ""}`}
          onClick={() => setView("radar")}
          aria-current={view === "radar" ? "page" : undefined}
        >
          Radar
        </button>
        <button
          className={`sa-nav-btn${view === "admin" ? " sa-nav-btn--active" : ""}`}
          onClick={() => setView("admin")}
          aria-current={view === "admin" ? "page" : undefined}
        >
          O.R.A. Control
        </button>
      </nav>

      <main className={`sa-main${view === "radar" ? " sa-main--radar" : ""}`}>
        {view === "radar" ? (
          <ErrorBoundary>
            <StormAtlasRadarPanel
              manifest={manifest}
              error={displayError}
              loading={loading}
              activeSite={activeSite}
              frameLimit={frameLimit}
              onFrameLimitChange={setFrameLimit}
              onRetry={retry}
              onSwitchView={() => setView("admin")}
              onRadarSiteSelect={handleRadarSiteSelect}
            />
          </ErrorBoundary>
        ) : (
          <StormAtlasAdminPanel
            manifest={manifest}
            apiBase={API_BASE}
          />
        )}
      </main>

      <WhatsNewModal />
    </div>
  );
}
